import type { FormsV1 } from '@frontend/api-digital-forms';
import type { PaymentMethodSetupKind, SavedPaymentMethod } from '@frontend/payments-platform';
import type { AchOnFileValue, CardOnFileValue } from '../../../../../../types/form-field-state.types';

export type PaymentFieldType = 'cardOnFile' | 'achOnFile';

export type PaymentMethods = FormsV1.Types.GetPaymentMethodsIO['output'];
export type PaymentMethod = NonNullable<PaymentMethods['paymentMethods']>[number];

export type FetchPaymentMethods = (paymentMethodIds: string[]) => Promise<PaymentMethods>;

const PAYMENT_FIELD_TYPES = new Set<string>(['cardOnFile', 'achOnFile']);

export const isPaymentFieldType = (type: string): type is PaymentFieldType => PAYMENT_FIELD_TYPES.has(type);

export const PAYMENT_KIND: Record<PaymentFieldType, PaymentMethodSetupKind> = {
  cardOnFile: 'card',
  achOnFile: 'ach',
};

const asString = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

/** Normalizes any stored value (template default, draft answer, live value) into a complete card value. */
export const toCardValue = (raw: unknown): CardOnFileValue => {
  const value = asRecord(raw);
  return {
    country: asString(value.country),
    expiryMonth: asString(value.expiryMonth),
    expiryYear: asString(value.expiryYear),
    lastFour: asString(value.lastFour),
    nameOnCard: asString(value.nameOnCard),
    brand: asString(value.brand),
    paymentMethodId: asString(value.paymentMethodId),
  };
};

/** Normalizes any stored value (template default, draft answer, live value) into a complete ACH value. */
export const toAchValue = (raw: unknown): AchOnFileValue => {
  const value = asRecord(raw);
  return {
    accountType: asString(value.accountType),
    bankName: asString(value.bankName),
    created: asString(value.created),
    lastFour: asString(value.lastFour),
    paymentMethodId: asString(value.paymentMethodId),
  };
};

export const toPaymentValue = (type: PaymentFieldType, raw: unknown) =>
  type === 'cardOnFile' ? toCardValue(raw) : toAchValue(raw);

export const findPaymentMethod = (response: PaymentMethods | undefined, paymentMethodId: string) =>
  response?.paymentMethods?.find((method) => method.paymentMethodId === paymentMethodId);

export const toSavedPaymentMethod = (method: PaymentMethod, kind: PaymentMethodSetupKind): SavedPaymentMethod =>
  kind === 'card'
    ? {
        kind,
        paymentMethodId: asString(method.paymentMethodId),
        brand: asString(method.cardInfo?.brand),
        lastFour: asString(method.cardInfo?.lastFour),
        expMonth: asString(method.cardInfo?.expMonth),
        expYear: asString(method.cardInfo?.expYear),
        country: asString(method.cardInfo?.country),
      }
    : {
        kind,
        paymentMethodId: asString(method.paymentMethodId),
        bankName: asString(method.bankAccountInfo?.bankName),
        accountType: asString(method.bankAccountInfo?.type),
        lastFour: asString(method.bankAccountInfo?.lastFour),
        created: asString(method.created),
      };

export const toFieldValue = (method: SavedPaymentMethod, nameOnCard = ''): CardOnFileValue | AchOnFileValue =>
  method.kind === 'card'
    ? {
        country: method.country,
        expiryMonth: method.expMonth,
        expiryYear: method.expYear,
        lastFour: method.lastFour,
        brand: method.brand,
        nameOnCard: method.nameOnCard ?? nameOnCard,
        paymentMethodId: method.paymentMethodId,
      }
    : {
        accountType: method.accountType,
        bankName: method.bankName,
        created: method.created,
        lastFour: method.lastFour,
        paymentMethodId: method.paymentMethodId,
      };

/** Required-field rule (spec §A6/§B7). */
export const isPaymentValueSatisfied = (type: PaymentFieldType, raw: unknown) => {
  if (type === 'cardOnFile') {
    const value = toCardValue(raw);
    return !!(value.expiryMonth && value.expiryYear && value.lastFour && value.nameOnCard);
  }
  const value = toAchValue(raw);
  return !!(value.accountType && value.bankName && value.lastFour);
};

export const hasSavedPaymentMethod = (type: PaymentFieldType, raw: unknown) => !!toPaymentValue(type, raw).lastFour;

/** "M/YYYY" as submitted to the backend; empty when either part is missing. */
const formatSubmittedExpiry = (month: string, year: string) => (month && year ? `${month}/${year}` : '');

/** "MM/YYYY" for display. */
export const formatDisplayExpiry = (month: string, year: string) =>
  month && year ? `${month.padStart(2, '0')}/${year}` : '';

/** Masked summary sent on submit (spec §A8/§B9). The payment method reference is never submitted. */
export const serializePaymentSubmission = (type: PaymentFieldType, raw: unknown): string => {
  if (type === 'cardOnFile') {
    const value = toCardValue(raw);
    return JSON.stringify({
      country: value.country,
      expiry: formatSubmittedExpiry(value.expiryMonth, value.expiryYear),
      last4: value.lastFour,
      nameOnCard: value.nameOnCard,
      brand: value.brand,
    });
  }
  const value = toAchValue(raw);
  return JSON.stringify({
    accountType: value.accountType,
    bankName: value.bankName,
    created: value.created,
    lastFour: value.lastFour,
  });
};

/** Draft payload: the method reference only (+ cardholder name). `undefined` when nothing has been saved (§B8.1). */
export const toPaymentDraftAnswer = (type: PaymentFieldType, raw: unknown) => {
  const value = toPaymentValue(type, raw);
  if (!value.paymentMethodId) return undefined;
  if (type === 'cardOnFile') {
    const { nameOnCard } = value as CardOnFileValue;
    return nameOnCard
      ? { paymentMethodId: value.paymentMethodId, nameOnCard }
      : { paymentMethodId: value.paymentMethodId };
  }
  return { paymentMethodId: value.paymentMethodId };
};
